import { NamedEntity, PagedParams, PagedResponse, PersistentFile } from '@feature/utils';

export const userDocumentKindList = [
  'EUROPASS_CV',
  'LANGUAGE_CERTIFICATE',
  'PSYCHOPEDAGOGICAL_CERTIFICATE',
  'DIGITAL_COMPETENCES_CERTIFICATE',
] as const;

export type UserDocumentKind = (typeof userDocumentKindList)[number];

// Extensions are listed beside every MIME type on purpose: a file the OS has no registered type for
// arrives as application/octet-stream and a MIME-only filter hides it from the picker.
export const userDocumentAccept = [
  'application/pdf,.pdf',
  'image/png,.png',
  'image/jpeg,.jpg,.jpeg',
  'image/heic,.heic',
  'image/tiff,.tif,.tiff',
  'image/webp,.webp',
  'application/msword,.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx',
  'application/vnd.oasis.opendocument.text,.odt',
  'application/rtf,.rtf',
  'application/xml,.xml',
  'application/zip,application/x-zip-compressed,.zip',
  'application/x-7z-compressed,.7z',
  'application/vnd.rar,.rar',
].join(',');

export interface UserDocument {
  id: number;
  kind: UserDocumentKind;
  file: PersistentFile;
  uploadedBy: NamedEntity;
  uploadedAt: string;
}

export interface UserDocumentSlot {
  kind: UserDocumentKind;
  document: UserDocument | null;
}

export interface UserDocumentUploadRequest {
  kind: UserDocumentKind;
  file: File;
}

export interface UserDocumentDownload {
  kind: UserDocumentKind;
  fallbackFilename: string;
}

export interface UserDocumentsRow {
  // name is null when the user has neither first nor last name on record, which dev data still has.
  user: { id: number; name: string | null };
  faculty: NamedEntity | null;
  department: NamedEntity | null;
  discipline: NamedEntity | null;
  slots: UserDocumentSlot[];
}

export interface UserDocumentKindCount {
  kind: UserDocumentKind;
  uploaded: number;
}

export interface UserDocumentSummary {
  users: number;
  completeUsers: number;
  perKind: UserDocumentKindCount[];
}

/** The unit ids the server checks against the units the current role heads. */
export interface UserDocumentScope {
  facultyId: number | null;
  departmentId: number | null;
  disciplineId: number | null;
}

export interface UserDocumentAdminFilter extends UserDocumentScope {
  kind: UserDocumentKind | null;
  // true or null, never false: the serializer skips nulls, and `missingOnly=false` is not a filter the server has.
  missingOnly: true | null;
}

export type UserDocumentAdminParams = PagedParams<UserDocumentAdminFilter>;

export type UserDocumentAdminPage = PagedResponse<UserDocumentsRow>;

export interface UserDocumentExportParams extends UserDocumentAdminFilter {
  search: string | null;
  lang: string;
}

export interface UserDocumentAdminUploadRequest {
  userId: number;
  kind: UserDocumentKind;
  file: File;
}

export interface UserDocumentAdminDownload {
  userId: number;
  kind: UserDocumentKind;
  fallbackFilename: string;
}

export interface UserDocumentAdminDelete {
  userId: number;
  kind: UserDocumentKind;
}
